// =============================================================================
// features/sales/campaigns/wizard/types.ts — Tipos do estado do wizard
// `/campanhas/nova` (F35-S08, doc 26 §6.6).
//
// O estado vive inteiro em `WizardContent` (useState) — NUNCA perdido ao
// voltar uma etapa (DoD: "voltar não perde o preenchido"). Os campos que a
// API já suporta via `Campaign`/`AudienceSource` (packages/shared-schemas)
// são reaproveitados diretamente; nenhum tipo é redeclarado.
// =============================================================================

import type {
  AssignmentStrategy,
  AudienceCounts,
  AudienceFilter,
  CampaignWeeklyWindow,
} from '@elemento/shared-schemas';

import { FALLBACK_ORG_TIMEZONE } from '../../../../lib/orgDateTime';

export const WIZARD_STEP_IDS = [1, 2, 3, 4, 5, 6] as const;
export type WizardStepId = (typeof WIZARD_STEP_IDS)[number];

// ---------------------------------------------------------------------------
// Etapa 1 — Objetivo
// ---------------------------------------------------------------------------

export interface ObjectiveValues {
  cityId: string;
  name: string;
  description: string;
  productId: string;
  /** Vazio = o próprio ator assume o campo `ownerUserId` (omitido no POST). */
  ownerUserId: string;
  priority: number;
}

export const EMPTY_OBJECTIVE: ObjectiveValues = {
  cityId: '',
  name: '',
  description: '',
  productId: '',
  ownerUserId: '',
  priority: 0,
};

// ---------------------------------------------------------------------------
// Etapa 2 — Público (fonte)
// ---------------------------------------------------------------------------

export type AudienceSourceKind = 'filter' | 'manual' | 'import';

export interface AudienceFilterState {
  leadStatus: AudienceFilter['leadStatus'];
  assignedAgentIds: AudienceFilter['assignedAgentIds'];
  createdFrom: string;
  createdTo: string;
}

export const EMPTY_AUDIENCE_FILTER: AudienceFilterState = {
  leadStatus: undefined,
  assignedAgentIds: undefined,
  createdFrom: '',
  createdTo: '',
};

export interface AudienceState {
  kind: AudienceSourceKind;
  filter: AudienceFilterState;
  /** Texto cru digitado/colado pelo gestor — um id por linha. */
  manualLeadIdsRaw: string;
  /** `import_batches.id` de um lote já concluído. */
  importBatchId: string;
}

export const EMPTY_AUDIENCE: AudienceState = {
  kind: 'filter',
  filter: EMPTY_AUDIENCE_FILTER,
  manualLeadIdsRaw: '',
  importBatchId: '',
};

// ---------------------------------------------------------------------------
// Etapa 3 — Higiene (resultado, não input)
// ---------------------------------------------------------------------------

export interface HygieneResultState {
  requested: number;
  counts: AudienceCounts;
  willMaterialize: number;
  /** `true` quando o resultado já foi MATERIALIZADO (apply/import), não só prévia. */
  applied: boolean;
}

// ---------------------------------------------------------------------------
// Etapa 4 — Distribuição
// ---------------------------------------------------------------------------

export interface DistributionValues {
  assignmentStrategy: AssignmentStrategy;
}

export const EMPTY_DISTRIBUTION: DistributionValues = {
  assignmentStrategy: 'shared_queue',
};

// ---------------------------------------------------------------------------
// Etapa 5 — Cadência
// ---------------------------------------------------------------------------

export interface CadenceValues {
  weeklyWindow: CampaignWeeklyWindow;
  timezone: string;
  maxAttempts: number;
  minIntervalMinutes: number;
}

export const EMPTY_CADENCE: CadenceValues = {
  weeklyWindow: {},
  timezone: FALLBACK_ORG_TIMEZONE,
  maxAttempts: 5,
  minIntervalMinutes: 60,
};
